import * as React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import { aiApprovalsPath } from '@logic'

import { usePendingApprovalsCount } from '../hooks/usePendingApprovalsCount'

export type PendingApprovalsBadgeProps = {
  call: <T = unknown>(method: string, params?: unknown) => Promise<T>
  organizationId: string | null
  /** When false, polling is paused (e.g. gateway not connected). */
  enabled: boolean
  /** Render as a link to /ai/approvals. When false, renders the count inline without navigation. */
  asLink?: boolean
}

const Badge = styled.span<{ $empty: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 11px;
  font-family: 'NunitoSansSemiBold';
  background-color: ${(p) => (p.$empty ? p.theme.input.default : p.theme.warning)};
  color: ${(p) => p.theme.white};
  line-height: 1;
`

const BadgeLink = styled<any>(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: inherit;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${(p) => p.theme.white}, 0 0 0 4px ${(p) => p.theme.accent};
    border-radius: ${(p) => p.theme.radius};
  }
`

/**
 * FR-022: in-app indicator that pending approvals exist for the active org's
 * gateway. Mounted inside AdminNav chrome and (via T066) on the parent /ai
 * layout for in-app site admins.
 */
export function PendingApprovalsBadge({
  call,
  organizationId,
  enabled,
  asLink = true,
}: PendingApprovalsBadgeProps) {
  const { count } = usePendingApprovalsCount({ call, organizationId, enabled })

  if (!enabled) return null

  const empty = count === 0
  const label = empty
    ? 'No pending approvals'
    : `${count} pending approval${count === 1 ? '' : 's'}`

  const badge = (
    <Badge $empty={empty} aria-label={label} role="status">
      {count}
    </Badge>
  )

  if (!asLink) return badge

  return (
    <BadgeLink to={aiApprovalsPath} aria-label={label}>
      {badge}
    </BadgeLink>
  )
}
